import { Text } from "@/components/ui/baseUi/text"
import { useBoxSlotSidebar } from "@/modules/boxSlot/hooks/useBoxSlotSidebar"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { cn } from "@/modules/lib/utils"
import { NoteForAthletesInEvent } from "@/modules/programs/components/programSession/blocks/notes/noteForAthletesInEvent"
import { NoteForCoachesInEvent } from "@/modules/programs/components/programSession/blocks/notes/noteForCoachesInEvent"
import { getDisplayExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { displayTotalTime, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { LoaderCircle } from "lucide-react"
import styled from "styled-components"

type BoxProgrammationSessionEventProps = {
  session: BoxSessionDetailsEntity
  boxSlotId: string
  isUpdating?: boolean
  isDetailsOpen?: boolean
  className?: string
} & React.ComponentProps<"div">

export function BoxProgrammationSessionEvent({
  session,
  boxSlotId,
  isUpdating = false,
  isDetailsOpen = false,
  className,
  ...props
}: BoxProgrammationSessionEventProps) {
  const { id, name, blocks, cardioTag, gymnasticTag, weightliftingTag } = session
  const shouldDisplayTags = cardioTag || gymnasticTag || weightliftingTag
  const { navigateToBoxSessionView } = useBoxSlotSidebar()

  const onClickOnSession = () => {
    navigateToBoxSessionView(session.id, boxSlotId)
  }

  return (
    <Container
      key={id}
      id={id}
      className={cn("cursor-pointer gap-2 border-b p-2", className)}
      onClick={onClickOnSession}
      $isDetailsOpen={isDetailsOpen}
      {...props}
    >
      <div className="flex flex-row items-center justify-between gap-2">
        <ProgramName fontStyle="TS" className="event-title text-textDefault-body" $isAuthor={true}>
          {name}
        </ProgramName>
        {isUpdating && <LoaderCircle className="animate-spin" />}
      </div>

      {shouldDisplayTags && (
        <div className="mt-1 flex flex-row items-center justify-start gap-2">
          {gymnasticTag && <GymSelectedIcon color="" width={25} height={25} />}
          {weightliftingTag && <WeightliftingSelectedIcon color="" width={25} height={25} />}
          {cardioTag && <CardioSelectedIcon color="" width={25} height={25} />}
        </div>
      )}

      {blocks.length > 0 && (
        <ExerciceStack>
          {blocks.map((block, blockIndex) =>
            block.type === SessionBlockType.SESSION_EXERCISE ? (
              <div className="flex flex-col gap-2" key={blockIndex}>
                <Text fontStyle="TS" className="event-category">
                  {getDisplayExerciseCategory(block.content.category, block.content.otherCategory)}
                </Text>
                {block.content.chrono?.type && (
                  <div className="flex items-center">
                    <ChronoIcon color="currentColor" className="text-textDefault-body" width={14} height={14} />
                    <div className="flex flex-col">
                      <Text fontStyle="BS" className="event-content ml-2 text-textDefault-body">
                        {getTrainingChronoTitle(block.content.chrono.type)}
                      </Text>
                      <Text fontStyle="BS" className="event-content ml-2 text-textDefault-body">
                        {displayTotalTime(block.content.chrono)}
                      </Text>
                    </div>
                  </div>
                )}
                <HURichTextView
                  value={formatTextToRichText(block.content.description)}
                  paragrapheFontStyle="BS"
                  className="event-content"
                />
              </div>
            ) : block.type === SessionBlockType.COACH_NOTE ? (
              <NoteForCoachesInEvent key={blockIndex} note={block.content} displayDetailedEvents />
            ) : (
              block.type === SessionBlockType.ATHLETE_NOTE && (
                <NoteForAthletesInEvent key={blockIndex} note={block.content} displayDetailedEvents />
              )
            ),
          )}
        </ExerciceStack>
      )}
    </Container>
  )
}

const Container = styled.div<{ $isDetailsOpen: boolean }>`
  background-color: ${({ $isDetailsOpen }) => ($isDetailsOpen ? "var(--primary-100)" : "transparent")};

  &:hover {
    .event-title {
      font-size: 1.05rem;
    }

    .hidden-session-icon i {
      color: var(--secondary-300);
    }
  }
`

const ProgramName = styled(Text)<{ $isAuthor: boolean }>`
  background-color: ${({ $isAuthor }) => ($isAuthor ? "var(--neutral-100)" : "var(--secondary-500)")};
  padding: 3px 8px;
  border-radius: 15px;
  line-height: 1rem;
`

const ExerciceStack = styled.div`
  position: relative;
  margin-top: 0.5rem;

  .hidden-session-icon {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    & > i {
      color: var(--secondary-200);
      font-size: 2rem;
    }
  }
`
